import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { select } from "d3-selection";
import "d3-transition"; // augments Selection with .transition() for the smooth zoom-control jumps below
import { zoom, zoomIdentity, type D3ZoomEvent, type ZoomBehavior, type ZoomTransform } from "d3-zoom";
import { api, type SeatStatus } from "../lib/api";
import { useAuth } from "../lib/auth";
import { buildCategoricalScale, buildGroupedCategoricalScale } from "../lib/color";
import { seatFill, seatBorder, isNonSeatable, type ColorMode } from "../lib/seatVisuals";
import { aggregateByBlock, aggregateByRow, type Aggregate } from "../lib/seatAggregation";
import { breakdownFor } from "../lib/breakdown";
import { loadHallPlanViewState, saveHallPlanViewState } from "../lib/viewState";
import { loadHallPlanPreferences, saveHallPlanPreferences, type HoverHighlightMode } from "../lib/hallPlanPreferences";
import WarningsPanel from "../components/WarningsPanel";
import ExportMenu from "../components/ExportMenu";
import SeatMatrixEditBar from "../components/SeatMatrixEditBar";
import type { TFunction } from "i18next";

const COLOR_MODES: ColorMode[] = ["occupancy", "registrationStatus", "region", "bezirk"];

/** Rebuilds a real ZoomTransform from stored (k, x, y) — see the math note by its call site. */
function restoreTransform(saved: { k: number; x: number; y: number } | null): ZoomTransform {
  if (!saved) return zoomIdentity;
  return zoomIdentity.scale(saved.k).translate(saved.x / saved.k, saved.y / saved.k);
}

type SeatingResponse = Awaited<ReturnType<typeof api.sessionSeating>>;
type Seat = SeatingResponse["seats"][number];
type Hovered = { kind: "seat"; seat: Seat; x: number; y: number } | { kind: "row"; agg: Aggregate; x: number; y: number };

const VIEW_WIDTH = 1000;
const VIEW_HEIGHT = 700;

// Three detail levels, matching the "Zoom-Konzept" from the architecture
// proposal: below the block threshold show only block outlines + occupancy;
// below the row threshold show row clusters; above that, individual seats.
// The two thresholds are user-configurable (see hallPlanPreferences.ts) —
// these are just the fallback defaults. Seat number text only renders once
// seats are large enough to read.
const SEAT_LABEL_MIN_SCALE = 8;

// Matches the d3-zoom scaleExtent below, expressed as percent for the zoom control.
const ZOOM_MIN_PERCENT = 80;
const ZOOM_MAX_PERCENT = 4000;
const ZOOM_STEP_FACTOR = 1.3;

// How many seats to zoom into when the participant search jumps to one —
// always at least deep enough to force individual-seat LOD, regardless of
// the user's own (configurable) row->seat threshold.
const SEARCH_ZOOM_MARGIN_PERCENT = 100;
const SEARCH_ZOOM_MIN_PERCENT = 600;
const SEARCH_RESULT_LIMIT = 8;

const REGISTRATION_STATUS_LEGEND_KEY: Record<string, string> = { Zusage: "zusage", Absage: "absage", offen: "offen" };

/** Maps a breakdown key (occupancy category, registration status, or a raw region/bezirk name) to display text. */
function legendLabelFor(mode: ColorMode, key: string, t: TFunction): string {
  if (mode === "occupancy") return t(`hallPlan.legend.${key}`, key);
  if (mode === "registrationStatus") return t(`hallPlan.legend.${REGISTRATION_STATUS_LEGEND_KEY[key] ?? key}`, key);
  return key; // region/bezirk are proper nouns, shown as-is
}

/** Renders SVG text at a constant on-screen size regardless of the current zoom scale. */
function ZoomInvariantLabel({ x, y, k, className, children }: { x: number; y: number; k: number; className: string; children: React.ReactNode }) {
  return (
    <g transform={`translate(${x},${y}) scale(${1 / k})`}>
      <text className={className} textAnchor="middle" dominantBaseline="middle">
        {children}
      </text>
    </g>
  );
}

export default function HallPlanPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { eventSessionId } = useParams();
  const [seats, setSeats] = useState<Seat[] | null>(null);
  const [warnings, setWarnings] = useState<SeatingResponse["warnings"]>([]);
  const [showWarningsPanel, setShowWarningsPanel] = useState(false);
  const [colorMode, setColorMode] = useState<ColorMode>(() => {
    const saved = eventSessionId ? loadHallPlanViewState(eventSessionId) : null;
    return saved && COLOR_MODES.includes(saved.colorMode as ColorMode) ? (saved.colorMode as ColorMode) : "occupancy";
  });
  const [transform, setTransform] = useState<ZoomTransform>(() => restoreTransform(eventSessionId ? loadHallPlanViewState(eventSessionId) : null));
  const [hovered, setHovered] = useState<Hovered | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [filterRegion, setFilterRegion] = useState<string | null>(null);
  const [filterBezirk, setFilterBezirk] = useState<string | null>(null);
  const [filterZielgruppe, setFilterZielgruppe] = useState<string | null>(null);
  const [zoomInput, setZoomInput] = useState(() => String(Math.round(transform.k * 100)));
  const [prefs, setPrefs] = useState(() => loadHallPlanPreferences());
  const [showLodSettings, setShowLodSettings] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchActiveIndex, setSearchActiveIndex] = useState(0);
  const [highlightedSeatId, setHighlightedSeatId] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const zoomBehaviorRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const zoomInputFocused = useRef(false);
  // The view the user was looking at right before the first search jump —
  // restored by the reset button so "back to normal view" means exactly that,
  // not a hard reset to the overview.
  const preSearchTransformRef = useRef<ZoomTransform | null>(null);

  const canEditMatrix = hasPermission("seat.matrix.edit");

  // Persist hover-highlight mode + LOD thresholds — these are user-level
  // preferences that apply across every hall plan, not per-session state
  // (see viewState.ts for the per-session coloring/zoom equivalent).
  useEffect(() => {
    saveHallPlanPreferences(prefs);
  }, [prefs]);

  function setHoverHighlightMode(mode: HoverHighlightMode) {
    setPrefs((prev) => ({ ...prev, hoverHighlightMode: mode }));
  }

  function setLodBlockMaxPercent(value: number) {
    setPrefs((prev) => ({ ...prev, lodBlockMaxPercent: Math.min(value, prev.lodRowMaxPercent - 10) }));
  }

  function setLodRowMaxPercent(value: number) {
    setPrefs((prev) => ({ ...prev, lodRowMaxPercent: Math.max(value, prev.lodBlockMaxPercent + 10) }));
  }

  // Keep the zoom control's number in sync with wheel/pinch/drag zooming,
  // but don't fight the user while they're actively typing a value in.
  useEffect(() => {
    if (!zoomInputFocused.current) setZoomInput(String(Math.round(transform.k * 100)));
  }, [transform.k]);

  function applyZoomPercent(percent: number) {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    const clamped = Math.min(ZOOM_MAX_PERCENT, Math.max(ZOOM_MIN_PERCENT, percent));
    select(svgRef.current).transition().duration(150).call(zoomBehaviorRef.current.scaleTo, clamped / 100);
  }

  function zoomByFactor(factor: number) {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    select(svgRef.current).transition().duration(150).call(zoomBehaviorRef.current.scaleBy, factor);
  }

  function commitZoomInput() {
    const parsed = Number.parseFloat(zoomInput.replace(",", "."));
    if (Number.isFinite(parsed)) applyZoomPercent(parsed);
    else setZoomInput(String(Math.round(transform.k * 100)));
  }

  // Region and Bezirk stay consistent with each other: picking a Region
  // narrows the Bezirk dropdown to that region's own districts (clearing the
  // current Bezirk if it no longer fits); picking a Bezirk snaps the Region
  // dropdown to that Bezirk's parent, since a Bezirk always belongs to
  // exactly one Region.
  function handleRegionFilterChange(value: string) {
    const region = value || null;
    setFilterRegion(region);
    if (region && filterBezirk && taxonomy.bezirkToRegion.get(filterBezirk) !== region) {
      setFilterBezirk(null);
    }
  }

  function handleBezirkFilterChange(value: string) {
    const bezirk = value || null;
    setFilterBezirk(bezirk);
    if (bezirk) {
      const region = taxonomy.bezirkToRegion.get(bezirk);
      if (region) setFilterRegion(region);
    }
  }

  // Zielgruppe is a fully independent filter dimension — unlike Region/Bezirk
  // it doesn't narrow or get narrowed by anything else, it just ANDs onto
  // whatever else is selected (see matchesFilter below).
  function handleZielgruppeFilterChange(value: string) {
    setFilterZielgruppe(value || null);
  }

  function toggleSeatSelection(seatId: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(seatId)) next.delete(seatId);
      else next.add(seatId);
      return next;
    });
  }

  async function applyStatusToSelection(status: SeatStatus, note?: string) {
    const ids = [...selectedIds];
    await api.bulkUpdateSeatStatus(ids, status, note);
    // Mirrors tooltipLabelFor() on the backend: only "reserved" keeps a note, any other status clears it.
    const tooltipLabel = status === "reserved" ? note?.trim() || null : null;
    setSeats((prev) => prev?.map((seat) => (selectedIds.has(seat.id) ? { ...seat, status, tooltipLabel } : seat)) ?? prev);
    setSelectedIds(new Set());
  }

  // Jumps the view to a specific seat: zooms in deep enough to force
  // seat-level LOD and pans so the seat lands in the center of the viewport.
  // The math mirrors restoreTransform's inverse — see the note there — since
  // the group is rendered as `translate(x,y) scale(k)`, centering seat
  // world-coords (wx,wy) needs x = VIEW_WIDTH/2 - wx*k (and same for y).
  function jumpToSeat(seat: Seat) {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    if (highlightedSeatId === null) preSearchTransformRef.current = transform;
    const targetPercent = Math.min(ZOOM_MAX_PERCENT, Math.max(SEARCH_ZOOM_MIN_PERCENT, prefs.lodRowMaxPercent + SEARCH_ZOOM_MARGIN_PERCENT));
    const k = targetPercent / 100;
    const worldX = seat.positionX * VIEW_WIDTH;
    const worldY = seat.positionY * VIEW_HEIGHT;
    const target = zoomIdentity.translate(VIEW_WIDTH / 2 - worldX * k, VIEW_HEIGHT / 2 - worldY * k).scale(k);
    select(svgRef.current).transition().duration(500).call(zoomBehaviorRef.current.transform, target);
    setHighlightedSeatId(seat.id);
    setSearchQuery(`${seat.occupant?.vorname ?? ""} ${seat.occupant?.nachname ?? ""}`.trim());
    setSearchOpen(false);
  }

  function resetSearch() {
    // Only snap the view back if a jump actually happened — clicking Reset
    // after merely typing (without picking a result) should just clear the
    // text, not disturb wherever the user had manually panned/zoomed to.
    if (highlightedSeatId !== null && svgRef.current && zoomBehaviorRef.current) {
      const target = preSearchTransformRef.current ?? zoomIdentity;
      select(svgRef.current).transition().duration(500).call(zoomBehaviorRef.current.transform, target);
    }
    preSearchTransformRef.current = null;
    setHighlightedSeatId(null);
    setSearchQuery("");
    setSearchOpen(false);
  }

  useEffect(() => {
    if (!eventSessionId) return;
    api.sessionSeating(Number(eventSessionId)).then((res) => {
      setSeats(res.seats);
      setWarnings(res.warnings);
    });
  }, [eventSessionId]);

  // Remember the coloring + zoom/pan per session, so a reload lands back
  // where the user left off. Debounced: transform changes on every wheel
  // tick during an active zoom/pan gesture, and writing on each of those is
  // unnecessary — only the value ~200ms after the user stops matters.
  useEffect(() => {
    if (!eventSessionId) return;
    const handle = setTimeout(() => {
      saveHallPlanViewState(eventSessionId, { colorMode, k: transform.k, x: transform.x, y: transform.y });
    }, 200);
    return () => clearTimeout(handle);
  }, [eventSessionId, colorMode, transform]);

  useEffect(() => {
    if (!svgRef.current) return;
    const svgSel = select(svgRef.current);
    const behavior = zoom<SVGSVGElement, unknown>()
      .scaleExtent([ZOOM_MIN_PERCENT / 100, ZOOM_MAX_PERCENT / 100])
      .on("zoom", (event: D3ZoomEvent<SVGSVGElement, unknown>) => setTransform(event.transform));
    zoomBehaviorRef.current = behavior;
    svgSel.call(behavior);
    // Sync d3-zoom's own internal transform to whatever we restored from
    // localStorage (or identity, if this is a first visit) — otherwise the
    // next wheel/drag gesture would jump back from d3's untouched default.
    svgSel.call(behavior.transform, transform);
    return () => {
      svgSel.on(".zoom", null);
      zoomBehaviorRef.current = null;
    };
  }, []);

  // Region/Bezirk/Zielgruppe taxonomy derived once from the loaded seats —
  // backs the color scales below AND the filter dropdowns further down.
  const taxonomy = useMemo(() => {
    const regionsSet = new Set<string>();
    const bezirkToRegion = new Map<string, string>();
    const bezirkeByRegion = new Map<string, string[]>();
    const zielgruppenSet = new Set<string>();
    for (const seat of seats ?? []) {
      const region = seat.occupant?.region;
      const bezirk = seat.occupant?.bezirk;
      if (region) regionsSet.add(region);
      if (bezirk && region) {
        bezirkToRegion.set(bezirk, region);
        const list = bezirkeByRegion.get(region) ?? [];
        if (!list.includes(bezirk)) list.push(bezirk);
        bezirkeByRegion.set(region, list);
      }
      if (seat.occupant?.zielgruppe) zielgruppenSet.add(seat.occupant.zielgruppe);
    }
    const regions = [...regionsSet].sort();
    for (const list of bezirkeByRegion.values()) list.sort();
    const allBezirke = [...bezirkToRegion.keys()].sort();
    const zielgruppen = [...zielgruppenSet].sort();
    return { regions, bezirkToRegion, bezirkeByRegion, allBezirke, zielgruppen };
  }, [seats]);

  const scales = useMemo(() => {
    if (!seats) return { region: new Map(), bezirkBorder: new Map() };
    const regions = seats.map((s) => s.occupant?.region).filter((v): v is string => Boolean(v));
    const bezirke = seats.map((s) => s.occupant?.bezirk).filter((v): v is string => Boolean(v));
    // Region drives the fill; Bezirk gets its own border color, spread
    // evenly WITHIN each region's own districts (not one shared rotation
    // across all ~130 districts) — see lib/color.ts buildGroupedCategoricalScale.
    return { region: buildCategoricalScale(regions), bezirkBorder: buildGroupedCategoricalScale(bezirke, (b) => taxonomy.bezirkToRegion.get(b)) };
  }, [seats, taxonomy]);

  // How many seated occupants share a given Bezirk — shown in the seat
  // tooltip so a Bezirk's total headcount is visible without leaving the
  // hall plan.
  const bezirkSeatedCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const seat of seats ?? []) {
      if (seat.occupant?.bezirk) counts.set(seat.occupant.bezirk, (counts.get(seat.occupant.bezirk) ?? 0) + 1);
    }
    return counts;
  }, [seats]);

  // Single-field search across name and participant ID, backed by the seats
  // already loaded for this session — no extra API round trip needed.
  const searchMatches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return (seats ?? [])
      .filter((seat): seat is Seat & { occupant: NonNullable<Seat["occupant"]> } => Boolean(seat.occupant))
      .filter((seat) => {
        const fullName = `${seat.occupant.vorname ?? ""} ${seat.occupant.nachname ?? ""}`.toLowerCase();
        return fullName.includes(q) || String(seat.occupant.participantsId).includes(q);
      })
      .slice(0, SEARCH_RESULT_LIMIT);
  }, [seats, searchQuery]);

  const fillOf = (seat: Seat) => seatFill(seat, colorMode, scales);

  const lodBlockMax = prefs.lodBlockMaxPercent / 100;
  const lodRowMax = prefs.lodRowMaxPercent / 100;
  const lod: "block" | "row" | "seat" = transform.k < lodBlockMax ? "block" : transform.k < lodRowMax ? "row" : "seat";

  const blockAggregates = useMemo(() => (seats && lod === "block" ? aggregateByBlock(seats, fillOf) : []), [seats, lod, colorMode, scales]);
  const rowAggregates = useMemo(() => (seats && lod === "row" ? aggregateByRow(seats, fillOf) : []), [seats, lod, colorMode, scales]);

  const showSeatLabels = transform.k >= SEAT_LABEL_MIN_SCALE;

  // Hovering a seated person dims every other seat, so everyone sharing the
  // same taxonomy value stands out immediately, however scattered they are.
  // Which taxonomy — Bezirk or Region — is user-configurable via
  // prefs.hoverHighlightMode. The Region/Bezirk filter dropdowns do the same
  // thing, but persistently and at whatever LOD is active — a selected
  // Bezirk always wins over a merely selected Region (it's the more specific
  // choice). A filter first excludes non-matching seats; hover then layers
  // on top of what's left.
  const hoveredHighlightValue =
    hovered?.kind === "seat" ? (prefs.hoverHighlightMode === "region" ? hovered.seat.occupant?.region : hovered.seat.occupant?.bezirk) : undefined;
  const filterActive = filterRegion !== null || filterBezirk !== null || filterZielgruppe !== null;

  // Region/Bezirk filter as before (Bezirk, being more specific, is checked
  // instead of Region rather than in addition to it). Zielgruppe is a
  // separate, independent dimension: whenever it's set it ANDs onto whatever
  // Region/Bezirk decided, rather than interacting with them.
  function matchesFilter(seat: Seat): boolean {
    if (filterBezirk && seat.occupant?.bezirk !== filterBezirk) return false;
    if (!filterBezirk && filterRegion && seat.occupant?.region !== filterRegion) return false;
    if (filterZielgruppe && seat.occupant?.zielgruppe !== filterZielgruppe) return false;
    return true;
  }

  function isSeatDimmed(seat: Seat): boolean {
    // A search hit takes over completely — it's the one thing the user asked
    // to see, so filters/hover dimming don't get a say while it's active.
    if (highlightedSeatId !== null) return seat.id !== highlightedSeatId;
    if (filterActive && !matchesFilter(seat)) return true;
    if (!hoveredHighlightValue) return false;
    return (prefs.hoverHighlightMode === "region" ? seat.occupant?.region : seat.occupant?.bezirk) !== hoveredHighlightValue;
  }

  // Block/row aggregates need the same highlight-priority rule as
  // isSeatDimmed — otherwise zooming out after a search jump (into block or
  // row LOD, where individual seats aren't rendered at all) silently drops
  // the highlight instead of carrying it over to the group containing it.
  function isAggHighlighted(agg: Aggregate): boolean {
    return highlightedSeatId !== null && agg.seats.some((s) => s.id === highlightedSeatId);
  }

  function isAggDimmed(agg: Aggregate): boolean {
    if (highlightedSeatId !== null) return !isAggHighlighted(agg);
    return filterActive && !agg.seats.some(matchesFilter);
  }

  const bezirkOptions = filterRegion ? (taxonomy.bezirkeByRegion.get(filterRegion) ?? []) : taxonomy.allBezirke;

  return (
    <div style={{ height: "100%", display: "grid", gridTemplateRows: "auto 1fr" }}>
      <div className="hallplan-toolbar">
        <h1 style={{ fontSize: "1rem", margin: 0 }}>{t("hallPlan.heading")}</h1>
        <div className="seat-search">
          <input
            type="text"
            className="seat-search-input"
            aria-label={t("hallPlan.search.placeholder")}
            placeholder={t("hallPlan.search.placeholder")}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSearchOpen(true);
              setSearchActiveIndex(0);
              if (highlightedSeatId !== null) setHighlightedSeatId(null);
            }}
            onFocus={() => setSearchOpen(true)}
            onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setSearchActiveIndex((i) => Math.min(i + 1, searchMatches.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSearchActiveIndex((i) => Math.max(i - 1, 0));
              } else if (e.key === "Enter") {
                e.preventDefault();
                const match = searchMatches[Math.min(searchActiveIndex, searchMatches.length - 1)];
                if (match) jumpToSeat(match);
              } else if (e.key === "Escape") {
                setSearchOpen(false);
              }
            }}
          />
          {searchOpen && searchQuery.trim() !== "" && (
            <ul className="seat-search-results">
              {searchMatches.length > 0 ? (
                searchMatches.map((seat, i) => (
                  <li key={seat.id} className={i === searchActiveIndex ? "active" : undefined} onMouseDown={() => jumpToSeat(seat)}>
                    <span className="seat-search-result-name">
                      {seat.occupant.vorname} {seat.occupant.nachname}
                    </span>
                    <span className="seat-search-result-meta">
                      #{seat.occupant.participantsId} · {seat.block}-{seat.row}-{seat.seatNumber}
                    </span>
                  </li>
                ))
              ) : (
                <li className="seat-search-no-results">{t("hallPlan.search.noResults")}</li>
              )}
            </ul>
          )}
        </div>
        {(highlightedSeatId !== null || searchQuery.trim() !== "") && (
          <button type="button" className="seat-search-reset" onClick={resetSearch}>
            {t("hallPlan.search.reset")}
          </button>
        )}
        <label>
          {t("hallPlan.colorMode.label")}:{" "}
          <select value={colorMode} onChange={(e) => setColorMode(e.target.value as ColorMode)}>
            <option value="occupancy">{t("hallPlan.colorMode.occupancy")}</option>
            <option value="registrationStatus">{t("hallPlan.colorMode.registrationStatus")}</option>
            <option value="region">{t("hallPlan.colorMode.region")}</option>
            <option value="bezirk">{t("hallPlan.colorMode.bezirk")}</option>
          </select>
        </label>
        <label>
          {t("hallPlan.hoverHighlight.label")}:{" "}
          <select value={prefs.hoverHighlightMode} onChange={(e) => setHoverHighlightMode(e.target.value as HoverHighlightMode)}>
            <option value="bezirk">{t("hallPlan.hoverHighlight.bezirk")}</option>
            <option value="region">{t("hallPlan.hoverHighlight.region")}</option>
          </select>
        </label>
        <label>
          {t("hallPlan.filter.regionLabel")}:{" "}
          <select value={filterRegion ?? ""} onChange={(e) => handleRegionFilterChange(e.target.value)}>
            <option value="">{t("hallPlan.filter.all")}</option>
            {taxonomy.regions.map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("hallPlan.filter.bezirkLabel")}:{" "}
          <select value={filterBezirk ?? ""} onChange={(e) => handleBezirkFilterChange(e.target.value)}>
            <option value="">{t("hallPlan.filter.all")}</option>
            {bezirkOptions.map((bezirk) => (
              <option key={bezirk} value={bezirk}>
                {bezirk}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("hallPlan.filter.zielgruppeLabel")}:{" "}
          <select value={filterZielgruppe ?? ""} onChange={(e) => handleZielgruppeFilterChange(e.target.value)}>
            <option value="">{t("hallPlan.filter.all")}</option>
            {taxonomy.zielgruppen.map((zielgruppe) => (
              <option key={zielgruppe} value={zielgruppe}>
                {zielgruppe}
              </option>
            ))}
          </select>
        </label>
        <div className="zoom-control">
          <button type="button" aria-label={t("hallPlan.zoom.out")} onClick={() => zoomByFactor(1 / ZOOM_STEP_FACTOR)}>
            −
          </button>
          <input
            type="text"
            inputMode="numeric"
            className="zoom-input"
            aria-label={t("hallPlan.zoom.label")}
            value={zoomInput}
            onFocus={() => {
              zoomInputFocused.current = true;
            }}
            onChange={(e) => setZoomInput(e.target.value)}
            onBlur={() => {
              zoomInputFocused.current = false;
              commitZoomInput();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
          />
          <span className="zoom-unit">%</span>
          <button type="button" aria-label={t("hallPlan.zoom.in")} onClick={() => zoomByFactor(ZOOM_STEP_FACTOR)}>
            +
          </button>
        </div>
        <div className="lod-settings">
          <button type="button" onClick={() => setShowLodSettings((v) => !v)}>
            {t("hallPlan.lod.toggle")}
          </button>
          {showLodSettings && (
            <div className="lod-settings-panel">
              <label>
                {t("hallPlan.lod.blockLabel")}
                <input
                  type="number"
                  inputMode="numeric"
                  min={ZOOM_MIN_PERCENT}
                  max={prefs.lodRowMaxPercent - 10}
                  value={prefs.lodBlockMaxPercent}
                  onChange={(e) => {
                    const value = Number.parseInt(e.target.value, 10);
                    if (Number.isFinite(value)) setLodBlockMaxPercent(Math.max(ZOOM_MIN_PERCENT, value));
                  }}
                />
                %
              </label>
              <label>
                {t("hallPlan.lod.rowLabel")}
                <input
                  type="number"
                  inputMode="numeric"
                  min={prefs.lodBlockMaxPercent + 10}
                  max={ZOOM_MAX_PERCENT}
                  value={prefs.lodRowMaxPercent}
                  onChange={(e) => {
                    const value = Number.parseInt(e.target.value, 10);
                    if (Number.isFinite(value)) setLodRowMaxPercent(Math.min(ZOOM_MAX_PERCENT, value));
                  }}
                />
                %
              </label>
              <button type="button" onClick={() => setPrefs((prev) => ({ ...prev, lodBlockMaxPercent: 150, lodRowMaxPercent: 300 }))}>
                {t("hallPlan.lod.reset")}
              </button>
            </div>
          )}
        </div>
        {warnings.length > 0 && (
          <button className="warnings-toggle" onClick={() => setShowWarningsPanel((v) => !v)}>
            {t("hallPlan.warnings", { count: warnings.length })}
          </button>
        )}
        {canEditMatrix && (
          <label className="edit-mode-toggle">
            <input
              type="checkbox"
              checked={editMode}
              onChange={(e) => {
                setEditMode(e.target.checked);
                setSelectedIds(new Set());
              }}
            />
            {t("hallPlan.edit.toggle")}
          </label>
        )}
        <div style={{ flex: 1 }} />
        {eventSessionId && <ExportMenu eventSessionId={Number(eventSessionId)} colorMode={colorMode} />}
      </div>

      <div className="hallplan-stage">
        {editMode && <div className="edit-mode-hint">{t("hallPlan.edit.hint")}</div>}
        <svg ref={svgRef} viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}>
          <g transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}>
            {lod === "block" &&
              blockAggregates.map((agg) => {
                const pad = 6;
                const x = agg.minX * VIEW_WIDTH - pad;
                const y = agg.minY * VIEW_HEIGHT - pad;
                const w = (agg.maxX - agg.minX) * VIEW_WIDTH + pad * 2;
                const h = (agg.maxY - agg.minY) * VIEW_HEIGHT + pad * 2;
                const cx = x + w / 2;
                const cy = y + h / 2;
                const pct = Math.round((agg.occupiedCount / agg.seatCount) * 100);
                const dimmed = isAggDimmed(agg);
                const highlighted = isAggHighlighted(agg);
                return (
                  <g key={agg.key} style={{ opacity: dimmed ? 0.25 : 1 }}>
                    <rect
                      className={highlighted ? "lod-block-rect lod-block-rect--highlighted" : "lod-block-rect"}
                      x={x}
                      y={y}
                      width={w}
                      height={h}
                      rx={6}
                      fill={agg.dominantFill}
                      fillOpacity={0.35}
                    />
                    <ZoomInvariantLabel x={cx} y={cy - 7} k={transform.k} className="lod-block-label">
                      {agg.label}
                    </ZoomInvariantLabel>
                    <ZoomInvariantLabel x={cx} y={cy + 8} k={transform.k} className="lod-block-badge">
                      {t("hallPlan.occupancyBadge", { occupied: agg.occupiedCount, total: agg.seatCount, pct })}
                    </ZoomInvariantLabel>
                  </g>
                );
              })}

            {lod === "row" &&
              rowAggregates.map((agg) => {
                const pad = 2;
                const x = agg.minX * VIEW_WIDTH - pad;
                const y = agg.minY * VIEW_HEIGHT - pad;
                const w = (agg.maxX - agg.minX) * VIEW_WIDTH + pad * 2;
                const h = (agg.maxY - agg.minY) * VIEW_HEIGHT + pad * 2;
                const dimmed = isAggDimmed(agg);
                const highlighted = isAggHighlighted(agg);
                return (
                  <g
                    key={agg.key}
                    style={{ opacity: dimmed ? 0.25 : 1 }}
                    onMouseEnter={(e) => setHovered({ kind: "row", agg, x: e.clientX, y: e.clientY })}
                    onMouseMove={(e) => setHovered({ kind: "row", agg, x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <rect
                      className={highlighted ? "lod-row-rect lod-row-rect--highlighted" : "lod-row-rect"}
                      x={x}
                      y={y}
                      width={Math.max(w, 2)}
                      height={Math.max(h, 2)}
                      rx={2}
                      fill={agg.dominantFill}
                    />
                    <ZoomInvariantLabel x={x - 10} y={y + h / 2} k={transform.k} className="lod-row-label">
                      {agg.label}
                    </ZoomInvariantLabel>
                  </g>
                );
              })}

            {lod === "seat" &&
              seats?.map((seat) => {
                const x = seat.positionX * VIEW_WIDTH;
                const y = seat.positionY * VIEW_HEIGHT;
                const size = 6;
                const selected = selectedIds.has(seat.id);
                const border = seatBorder(seat, colorMode, scales);
                const dimmed = isSeatDimmed(seat);
                return (
                  <g key={seat.id}>
                    {seat.id === highlightedSeatId && <circle className="seat-highlight-ring" cx={x} cy={y} r={size * 0.9} />}
                    <g
                      transform={`translate(${x - size / 2},${y - size / 2})`}
                      onMouseEnter={(e) => setHovered({ kind: "seat", seat, x: e.clientX, y: e.clientY })}
                      onMouseMove={(e) => setHovered({ kind: "seat", seat, x: e.clientX, y: e.clientY })}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => editMode && toggleSeatSelection(seat.id)}
                      style={editMode ? { cursor: "pointer", opacity: dimmed ? 0.25 : 1 } : { opacity: dimmed ? 0.25 : 1 }}
                    >
                      <rect
                        className={selected ? "seat-rect seat-rect--selected" : "seat-rect"}
                        width={size}
                        height={size}
                        rx={0.8}
                        fill={seatFill(seat, colorMode, scales)}
                        style={!selected && border ? { stroke: border, strokeWidth: 1.3 } : undefined}
                      />
                      {isNonSeatable(seat) && (
                        <g className="seat-blocked-mark">
                          <line x1={0.9} y1={0.9} x2={size - 0.9} y2={size - 0.9} />
                          <line x1={size - 0.9} y1={0.9} x2={0.9} y2={size - 0.9} />
                        </g>
                      )}
                    </g>
                    {showSeatLabels && (
                      <ZoomInvariantLabel x={x} y={y + size + 3} k={transform.k} className="seat-number-label">
                        {seat.seatNumber}
                      </ZoomInvariantLabel>
                    )}
                  </g>
                );
              })}
          </g>
        </svg>

        {showWarningsPanel && <WarningsPanel warnings={warnings} onClose={() => setShowWarningsPanel(false)} />}
        {editMode && <SeatMatrixEditBar selectedCount={selectedIds.size} onApply={applyStatusToSelection} onClear={() => setSelectedIds(new Set())} />}
      </div>

      {hovered?.kind === "seat" && (
        <div className="seat-tooltip" style={{ left: hovered.x + 12, top: hovered.y + 12 }}>
          <strong>{t("hallPlan.seatTooltip.seat", { block: hovered.seat.block, row: hovered.seat.row, seat: hovered.seat.seatNumber })}</strong>
          {hovered.seat.occupant ? (
            <div>
              {hovered.seat.occupant.vorname} {hovered.seat.occupant.nachname}
              <br />
              {t("hallPlan.seatTooltip.participantIdLabel")}: {hovered.seat.occupant.participantsId}
              <br />
              {hovered.seat.occupant.region} {hovered.seat.occupant.bezirk ? `· ${hovered.seat.occupant.bezirk}` : ""}
              {hovered.seat.occupant.zielgruppe && (
                <>
                  <br />
                  {t("hallPlan.seatTooltip.zielgruppeLabel")}: {hovered.seat.occupant.zielgruppe}
                </>
              )}
              <br />
              {hovered.seat.occupant.registrationStatus}
              {hovered.seat.occupant.bezirk && (
                <>
                  <br />
                  {t("hallPlan.seatTooltip.bezirkCount", { count: bezirkSeatedCounts.get(hovered.seat.occupant.bezirk) ?? 0, bezirk: hovered.seat.occupant.bezirk })}
                </>
              )}
            </div>
          ) : (
            <div>
              {hovered.seat.status === "available" ? t("hallPlan.seatTooltip.empty") : t(`hallPlan.legend.${hovered.seat.status}`)}
              {hovered.seat.status === "reserved" && hovered.seat.tooltipLabel && (
                <>
                  <br />
                  {hovered.seat.tooltipLabel}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {hovered?.kind === "row" && (
        <div className="seat-tooltip" style={{ left: hovered.x + 12, top: hovered.y + 12 }}>
          <strong>{t("hallPlan.rowTooltip.row", { block: hovered.agg.key.split("-")[0], row: hovered.agg.key.split("-").slice(1).join("-") })}</strong>
          <div>{t("hallPlan.rowTooltip.seats", { count: hovered.agg.seatCount })}</div>
          <div>
            {breakdownFor(hovered.agg.seats, colorMode, (key) => legendLabelFor(colorMode, key, t))
              .map((entry) => `${entry.count} ${entry.label}`)
              .join(" / ")}
          </div>
        </div>
      )}
    </div>
  );
}
